import {useEffect, useRef, useState} from 'react';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {ImageAttachment, MAX_IMAGES_PER_MESSAGE, normalizeImages} from '../types/feedback';
import {uploadImage, ImageUploadError} from '../services/imageUpload';

/**
 * An image slot in the editor: either an already-uploaded attachment (existing)
 * or a freshly-picked local File awaiting upload (pending).
 */
export interface ImageSlot {
  id: string;
  /** Local object URL (pending) or remote URL (existing). */
  previewUrl: string;
  file?: File;
  remoteUrl?: string;
  isHero: boolean;
}

let slotCounter = 0;
const nextSlotId = () => `slot_${Date.now()}_${slotCounter++}`;

export function imagesToSlots(images: ImageAttachment[]): ImageSlot[] {
  return images.map((img) => ({
    id: nextSlotId(),
    previewUrl: img.url,
    remoteUrl: img.url,
    isHero: img.isHero === true,
  }));
}

export function fileToSlot(file: File): ImageSlot {
  return {
    id: nextSlotId(),
    previewUrl: URL.createObjectURL(file),
    file,
    isHero: false,
  };
}

/**
 * Upload any pending (file-backed) slots and return the final ImageAttachment[].
 * Preserves order and hero flags. Existing slots are reused as-is.
 */
export async function resolveSlots(
  slots: ImageSlot[],
  storagePath: string,
): Promise<ImageAttachment[]> {
  const out: ImageAttachment[] = [];
  for (const slot of slots) {
    if (slot.remoteUrl) {
      out.push({url: slot.remoteUrl, ...(slot.isHero ? {isHero: true} : {})});
    } else if (slot.file) {
      const url = await uploadImage(slot.file, storagePath);
      out.push({url, ...(slot.isHero ? {isHero: true} : {})});
    }
  }
  return out;
}

export function revokeSlotPreviews(slots: ImageSlot[]) {
  for (const s of slots) {
    if (s.file && s.previewUrl) URL.revokeObjectURL(s.previewUrl);
  }
}

// ── Editor ────────────────────────────────────────────────────────────────

interface MessageImagesEditorProps {
  slots: ImageSlot[];
  onChange: (slots: ImageSlot[]) => void;
  disabled?: boolean;
  /** Shown as a ⭐ highlight color. Defaults to amber. */
  heroColor?: string;
}

/**
 * Grid of image thumbnails with ✕ (remove) and ⭐ (mark as hero) buttons.
 * The parent owns the slots state; this component only renders and dispatches
 * changes. Marking a slot as hero unmarks all others (single hero rule).
 */
export function MessageImagesEditor({
  slots,
  onChange,
  disabled,
  heroColor = '#f59e0b',
}: MessageImagesEditorProps) {
  const t = useTraduire();
  if (slots.length === 0) return null;

  const toggleHero = (id: string) => {
    const target = slots.find((s) => s.id === id);
    const wasHero = target?.isHero === true;
    onChange(
      slots.map((s) => ({
        ...s,
        isHero: s.id === id ? !wasHero : false,
      })),
    );
  };

  const remove = (id: string) => {
    const victim = slots.find((s) => s.id === id);
    if (victim?.file && victim.previewUrl) URL.revokeObjectURL(victim.previewUrl);
    onChange(slots.filter((s) => s.id !== id));
  };

  return (
    <div className="flex flex-wrap gap-2">
      {slots.map((slot) => (
        <div
          key={slot.id}
          className="relative group"
          style={slot.isHero ? {boxShadow: `0 0 0 2px ${heroColor}`, borderRadius: 6} : undefined}
        >
          <img
            src={slot.previewUrl}
            alt="Attachment"
            className="h-16 w-16 object-cover rounded border border-gray-200"
          />
          {/* Remove */}
          <button
            type="button"
            onClick={() => remove(slot.id)}
            disabled={disabled}
            className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[9px] flex items-center justify-center hover:bg-red-600 disabled:opacity-50"
            title={t(T.retirer)}
          >
            &times;
          </button>
          {/* Hero toggle */}
          <button
            type="button"
            onClick={() => toggleHero(slot.id)}
            disabled={disabled}
            className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-full text-[9px] flex items-center justify-center disabled:opacity-50"
            style={{
              backgroundColor: slot.isHero ? heroColor : '#e5e7eb',
              color: slot.isHero ? 'white' : '#6b7280',
            }}
            title={slot.isHero ? 'Unmark as hero' : 'Mark as hero image'}
          >
            ★
          </button>
        </div>
      ))}
    </div>
  );
}

// ── File input helper ─────────────────────────────────────────────────────

interface AttachButtonProps {
  slots: ImageSlot[];
  onChange: (slots: ImageSlot[]) => void;
  disabled?: boolean;
  onError?: (err: unknown) => void;
  buttonClassName?: string;
  iconClassName?: string;
}

/**
 * File input + attach button that appends newly-picked files to the slots
 * array, enforcing `MAX_IMAGES_PER_MESSAGE`. Validates picked files eagerly.
 */
export function AttachImagesButton({
  slots,
  onChange,
  disabled,
  onError,
  buttonClassName = 'text-gray-400 hover:text-primary transition-colors disabled:opacity-50',
  iconClassName = 'w-4 h-4',
}: AttachButtonProps) {
  const t = useTraduire();
  const inputRef = useRef<HTMLInputElement>(null);
  const remaining = MAX_IMAGES_PER_MESSAGE - slots.length;
  const isFull = remaining <= 0;

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    const accepted = files.slice(0, remaining);
    if (files.length > accepted.length && onError) {
      onError(
        new ImageUploadError(
          `Maximum ${MAX_IMAGES_PER_MESSAGE} images per message.`,
        ),
      );
    }
    onChange([...slots, ...accepted.map(fileToSlot)]);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleSelect}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isFull}
        className={buttonClassName}
        title={isFull ? `Max ${MAX_IMAGES_PER_MESSAGE} images` : t(T.joindreDesImages)}
      >
        <svg className={iconClassName} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      </button>
    </>
  );
}

// ── Display ───────────────────────────────────────────────────────────────

interface MessageHeroImageProps {
  images: ImageAttachment[] | undefined;
  /** Legacy single-image field for backward compatibility. */
  legacyImageUrl?: string;
  /** Tailwind border class for the hero frame. */
  borderClass?: string;
  /** Max height for hero image (Tailwind class). Defaults to max-h-80. */
  maxHeightClass?: string;
}

/**
 * Renders the single "hero" image (if any) full-width, to be placed above
 * the message text — blog-post style. Returns null if no hero is marked.
 * Handles legacy `imageUrl: string` messages transparently (legacy images
 * never had a hero flag, so this will return null for them).
 */
export function MessageHeroImage({
  images,
  legacyImageUrl,
  borderClass = 'border border-gray-200',
  maxHeightClass = 'max-h-80',
}: MessageHeroImageProps) {
  const normalized = normalizeImages(images, legacyImageUrl);
  const hero = normalized.find((img) => img.isHero);
  if (!hero) return null;

  return (
    <a href={hero.url} target="_blank" rel="noopener noreferrer" className="block mb-3 mt-1">
      <img
        src={hero.url}
        alt="Hero attachment"
        className={`w-full ${maxHeightClass} object-cover rounded-lg ${borderClass}`}
        loading="lazy"
        style={{
          boxShadow:
            '0 12px 28px -12px rgba(0, 0, 0, 0.18), 0 6px 14px -4px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
      />
    </a>
  );
}

interface MessageImagesDisplayProps {
  images: ImageAttachment[] | undefined;
  /** Legacy single-image field for backward compatibility. */
  legacyImageUrl?: string;
  /** Tailwind border class for thumbnails. */
  thumbnailBorderClass?: string;
  /** Max height for thumbnails (Tailwind class). Defaults to max-h-16. */
  thumbnailMaxHeightClass?: string;
}

/**
 * Renders the non-hero image thumbnails as a grid. Meant to be placed
 * below the message text. The hero image (if any) is rendered separately
 * by `MessageHeroImage` above the text. Handles legacy `imageUrl`.
 */
export function MessageImagesDisplay({
  images,
  legacyImageUrl,
  thumbnailBorderClass = 'border border-gray-200',
  thumbnailMaxHeightClass = 'max-h-16',
}: MessageImagesDisplayProps) {
  const normalized = normalizeImages(images, legacyImageUrl);
  const thumbnails = normalized.filter((img) => !img.isHero);
  if (thumbnails.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {thumbnails.map((img, idx) => (
        <a
          key={`${img.url}_${idx}`}
          href={img.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <img
            src={img.url}
            alt="Attachment"
            className={`${thumbnailMaxHeightClass} rounded ${thumbnailBorderClass} cursor-pointer hover:opacity-90`}
            loading="lazy"
          />
        </a>
      ))}
    </div>
  );
}

/**
 * React hook: manages an ImageSlot[] with automatic cleanup of object URLs
 * when the component unmounts.
 */
export function useImageSlots(initial: ImageSlot[] = []) {
  const [slots, setSlots] = useState<ImageSlot[]>(initial);
  // Cleanup all pending object URLs on unmount
  useEffect(() => {
    return () => revokeSlotPreviews(slots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [slots, setSlots] as const;
}
